import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as api from '@/api/flyers';
import type { FlyerCategoryInput, FlyerTemplateInput } from '@/types/flyer';

export const FLYER_CATEGORIES_KEY = ['flyer-categories'] as const;
export const FLYER_TEMPLATES_KEY = ['flyer-templates'] as const;
export const FLYER_TEMPLATE_KEY = (id: string) => ['flyer-template', id] as const;

export function useFlyerCategories() {
  return useQuery({ queryKey: FLYER_CATEGORIES_KEY, queryFn: api.listFlyerCategories });
}
export function useCreateFlyerCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (b: FlyerCategoryInput) => api.createFlyerCategory(b),
    onSuccess: () => qc.invalidateQueries({ queryKey: FLYER_CATEGORIES_KEY }),
  });
}
export function useUpdateFlyerCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, b }: { id: string; b: FlyerCategoryInput }) => api.updateFlyerCategory(id, b),
    onSuccess: () => qc.invalidateQueries({ queryKey: FLYER_CATEGORIES_KEY }),
  });
}
export function useDeleteFlyerCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deleteFlyerCategory(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: FLYER_CATEGORIES_KEY }),
  });
}
export function useFlyerTemplates() {
  return useQuery({ queryKey: FLYER_TEMPLATES_KEY, queryFn: api.listFlyerTemplates });
}
export function useFlyerTemplate(id: string | null, lang = 'en') {
  return useQuery({
    queryKey: [...FLYER_TEMPLATE_KEY(id ?? 'none'), lang],
    queryFn: () => api.getFlyerTemplate(id!, lang),
    enabled: !!id,
  });
}
export function useCreateFlyerTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (b: FlyerTemplateInput) => api.createFlyerTemplate(b),
    onSuccess: () => qc.invalidateQueries({ queryKey: FLYER_TEMPLATES_KEY }),
  });
}
export function useUpdateFlyerTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, b }: { id: string; b: FlyerTemplateInput }) => api.updateFlyerTemplate(id, b),
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: FLYER_TEMPLATES_KEY });
      qc.invalidateQueries({ queryKey: FLYER_TEMPLATE_KEY(v.id) });
    },
  });
}
export function useDeleteFlyerTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deleteFlyerTemplate(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: FLYER_TEMPLATES_KEY }),
  });
}
export function useSetTemplateBackground() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, generationId }: { id: string; generationId: string }) => api.setTemplateBackground(id, generationId),
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: FLYER_TEMPLATES_KEY });
      qc.invalidateQueries({ queryKey: FLYER_TEMPLATE_KEY(v.id) });
    },
  });
}
